import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChannelCategory } from '@models/models-validators/category-validator';
import { User } from '@models/user';
import { OpsUserCategoryService } from '@service/ops-user-category.service';
import { CATEGORY_MAX_LEN, ELLIPSIS } from 'icreate/common/models/ops-user';

@Component({
    selector: 'app-edit-permissions-dialog',
    templateUrl: './edit-permissions-dialog.component.html',
    styleUrls: ['./edit-permissions-dialog.component.css'],
    standalone: false,
})
export class EditPermissionDialogComponent {
    name: string = '';
    categoryList: any[] = [];
    opsUserId: string = '';
    userList: any[] = [];
    isModified: boolean = false;

    editChannels: boolean = false;
    controlMimics: boolean = false;

    headerCategories: { key: string; display: string }[] = [];
    showHeaderMenu: { [key: string]: boolean } = {};
    showRowMenu: boolean[] = [];

    currentPage = 0;
    pageSize = 3;
    CATEGORY_MAX_LEN = CATEGORY_MAX_LEN;
    ELLIPSIS = ELLIPSIS;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            userList: User[];
            categoryList: ChannelCategory[];
            id: string;
            name: string;
            editChannels: boolean;
            controlMimic: boolean;
            vesselId: string;
        },
        private dialogRef: MatDialogRef<EditPermissionDialogComponent>,
        private opsUserCategoryService: OpsUserCategoryService
    ) {
        this.userList = data.userList || [];
        this.categoryList = data.categoryList || [];
        this.opsUserId = data.id || '';
        this.name = data.name || '';
        this.editChannels = data.editChannels;
        this.controlMimics = data.controlMimic;

        this.headerCategories = this.categoryList.map(c => ({
            key: c.category.toLowerCase(),
            display: c.category,
        }));

        this.headerCategories.forEach(h => (this.showHeaderMenu[h.key] = false));
    }
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (target.closest('.more-options-container') || target.closest('mat-icon')) return;
        Object.keys(this.showHeaderMenu).forEach(k => (this.showHeaderMenu[k] = false));
        this.showRowMenu = this.showRowMenu.map(() => false);
    }

    ngOnInit() {
        this.userList = (this.data.userList || []).map(user => ({
            ...user,
            permissions: this.headerCategories.reduce((acc, h) => {
                acc[h.key] = { editChannels: false, controlMimic: false };
                return acc;
            }, {} as any),
        }));

        if (this.userList.length) {
            const vesselId = this.userList[0]?.vesselId;
            if (vesselId) {
                try {
                    const categoryMap = new Map(this.categoryList.map(c => [c.category.toLowerCase(), c]));

                    this.userList = this.data.userList.map(user => {
                        const permissions: any = {};
                        this.headerCategories.forEach(header => {
                            const categoryObj = categoryMap.get(header.key);
                            const userCategory = user.permissions?.find(p => p.categoryId === categoryObj.id);

                            permissions[header.key] = {
                                editChannels: !!userCategory?.editChannel,
                                controlMimic: !!userCategory?.controlMimic,
                            };
                            if (this.isAdmin(user)) {
                                permissions[header.key].editChannels = true;
                                permissions[header.key].controlMimic = true;
                            }
                        });

                        return {
                            ...user,
                            permissions: permissions,
                        };
                    });
                } catch (error) {
                    console.error('Failed to load channel categories:', error);
                }
            }
        }
    }

    isAdmin(user: any): boolean {
        return user.isAdmin;
    }

    cancelClick(result: boolean = false) {
        this.dialogRef.close(result);
    }

    async createButtonClick() {
        try {
            const payload = this.userList.flatMap(user =>
                this.headerCategories
                    .map(category => {
                        if (this.isAdmin(user)) return;
                        const categoryObj = this.categoryList.find(c => c.category.toLowerCase() === category.key);
                        if (!categoryObj) return null;

                        return {
                            id: undefined,
                            opsUserId: user.id,
                            categoryId: categoryObj.id,
                            editChannel: user.permissions?.[category.key]?.editChannels ?? null,
                            controlMimic: user.permissions?.[category.key]?.controlMimic ?? null,
                        };
                    })
                    .filter(Boolean)
            );

            await this.opsUserCategoryService.createMany(payload, this.data.vesselId);
        } catch (error) {
            console.error('Error while saving:', error);
        }
        this.dialogRef.close();
    }

    get categoryRangeLabel(): string {
        const start = this.currentPage * this.pageSize + 1;
        const end = Math.min((this.currentPage + 1) * this.pageSize, this.headerCategories.length);
        return `${start}-${end}`;
    }

    get pagedHeaderCategories() {
        const start = this.currentPage * this.pageSize;
        const pageItems = this.headerCategories.slice(start, start + this.pageSize);
        const emptySlots = this.pageSize - pageItems.length;
        return [...pageItems, ...Array(emptySlots).fill(null)];
    }

    nextPage() {
        if ((this.currentPage + 1) * this.pageSize < this.headerCategories.length) this.currentPage++;
    }

    prevPage() {
        if (this.currentPage > 0) this.currentPage--;
    }

    toggleMenu(target: any): void {
        if (typeof target === 'number') {
            Object.keys(this.showHeaderMenu).forEach(k => (this.showHeaderMenu[k] = false));
            while (this.showRowMenu.length <= target) this.showRowMenu.push(false);
            this.showRowMenu = this.showRowMenu.map((_, i) => (i === target ? !this.showRowMenu[i] : false));
        } else {
            Object.keys(this.showHeaderMenu).forEach(
                key => (this.showHeaderMenu[key] = key === target ? !this.showHeaderMenu[key] : false)
            );
            this.showRowMenu = this.showRowMenu.map(() => false);
        }
    }

    setEditChannelsForCategory(categoryKey: string) {
        this.toggleCategoryProperty(categoryKey, 'editChannels');
    }

    setControlMimicsForCategory(categoryKey: string) {
        this.toggleCategoryProperty(categoryKey, 'controlMimic');
    }

    private toggleCategoryProperty(categoryKey: string, property: 'editChannels' | 'controlMimic') {
        if (!this.userList || !this.userList.length) return;
        const allOn = this.userList.every(user => user.permissions[categoryKey][property]);
        const newValue = !allOn;
        this.userList = this.userList.map(user =>
            this.isAdmin(user)
                ? user
                : {
                      ...user,
                      permissions: {
                          ...user.permissions,
                          [categoryKey]: { ...user.permissions[categoryKey], [property]: newValue },
                      },
                  }
        );

        this.markAsModified();
    }

    setEditChannelsForUser(userIndex: number) {
        const user = this.userList[userIndex];
        if (this.isAdmin(user)) return;
        if (!user) return;

        const anyOff = this.headerCategories.some(header => !user.permissions[header.key].editChannels);
        const newValue = anyOff;
        this.headerCategories.forEach(header => {
            user.permissions[header.key].editChannels = newValue;
        });
        this.markAsModified();
    }

    setControlMimicsForUser(userIndex: number) {
        const user = this.userList[userIndex];
        if (this.isAdmin(user)) return;
        if (!user) return;

        const anyOff = this.headerCategories.some(header => !user.permissions[header.key].controlMimic);
        const newValue = anyOff;
        this.headerCategories.forEach(header => {
            user.permissions[header.key].controlMimic = newValue;
        });
        this.markAsModified();
    }

    markAsModified(): void {
        this.isModified = true;
    }
}
